import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Box, Typography, Grid, Button, Chip, Avatar,
  Skeleton, Tabs, Tab,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StarIcon from "@mui/icons-material/Star";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import FiberNewIcon from "@mui/icons-material/FiberNew";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import axios from "axios";
import CamEmbedModal from "./CamEmbedModal";
import { PreRollAd } from "./TwinredPreRoll";

const blink = keyframes`0%{opacity:0}50%{opacity:1}100%{opacity:0}`;
const shimmer = keyframes`0%{background-position:-200% 0}100%{background-position:200% 0}`;

const LiveDot = styled("span")({
  height: 8, width: 8, backgroundColor: "#4CAF50",
  borderRadius: "50%", display: "inline-block", marginRight: 6,
  animation: `${blink} 1.5s ease-in-out infinite`,
});

const RankBadge = styled(Box)(({ rank }) => ({
  position: "absolute", top: 8, left: 8, zIndex: 2,
  width: 28, height: 28, borderRadius: "50%",
  display: "flex", alignItems: "center", justifyContent: "center",
  fontWeight: 900, fontSize: "0.75rem",
  background: rank === 1 ? "linear-gradient(135deg,#FFD700,#FFA000)"
    : rank === 2 ? "linear-gradient(135deg,#C0C0C0,#9E9E9E)"
    : rank === 3 ? "linear-gradient(135deg,#CD7F32,#A0522D)"
    : "rgba(0,0,0,0.6)",
  color: rank <= 3 ? "#000" : "#fff",
  boxShadow: rank <= 3 ? "0 2px 8px rgba(0,0,0,0.3)" : "none",
}));

const PerformerTile = styled(Box)({
  position: "relative", borderRadius: 12, overflow: "hidden",
  cursor: "pointer", backgroundColor: "#111",
  "&:hover .overlay": { opacity: 1 },
  "&:hover img": { transform: "scale(1.05)" },
});

const Overlay = styled(Box)({
  position: "absolute", inset: 0, opacity: 0, transition: "opacity 0.2s",
  background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 60%)",
  display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: 12,
});

// Sort modes
const TABS = [
  { label: "🔥 Trending",   value: "trending",  icon: <WhatshotIcon sx={{ fontSize: 16 }} /> },
  { label: "⭐ Top Rated",  value: "rating",    icon: <StarIcon sx={{ fontSize: 16 }} /> },
  { label: "✨ New Girls",  value: "new",        icon: <FiberNewIcon sx={{ fontSize: 16 }} /> },
  { label: "🥇 Gold Shows", value: "gold",       icon: null },
];

const SORT_PARAMS = {
  trending:  { exact: false },
  rating:    { exact: false },
  new:       { newOnly: true, exact: false },
  gold:      { exact: false },
};

const TopPerformersPage = () => {
  const [tab, setTab]               = useState("trending");
  const [performers, setPerformers] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(1);
  const [hasMore, setHasMore]       = useState(true);
  const [embedP, setEmbedP]         = useState(null);
  const [preRollP, setPreRollP]     = useState(null);

  const fetchPerformers = async (tabVal, pageNum, append = false) => {
    setLoading(true);
    try {
      const params = { ...SORT_PARAMS[tabVal], limit: 24, page: pageNum };
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/performers`, { params });
      const list = Array.isArray(res.data) ? res.data : [];
      if (list.length < 24) setHasMore(false);

      const mapped = list.map((p, i) => {
        const a = p["@attributes"] || {};
        const thumb = p?.Media?.Pic?.Thumb?.["@attributes"]?.Src || "";
        const full  = p?.Media?.Pic?.Full?.["@attributes"]?.Src  || "";
        const gallery = (() => {
          const raw = p?.Media?.PhotoGallery?.Pic;
          if (!raw) return [];
          return (Array.isArray(raw) ? raw : [raw])
            .map(pic => pic?.Full?.["@attributes"]?.Src || "").filter(Boolean);
        })();
        return {
          id: a.Id, name: a.Name, age: a.Age,
          ethnicity: a.Ethnicity, hairColor: a.HairColor,
          goldShow: !!(a.PerfFlag && (parseInt(a.PerfFlag) & 0x00100000)),
          hdStream: !!(a.PerfFlag && (parseInt(a.PerfFlag) & 0x10000000)),
          about: p?.Descriptions?.About || "",
          thumb, full, gallery,
          rank: append ? null : (pageNum - 1) * 24 + i + 1,
          chatRoomUrl: `https://instacamgirlslive.com/cam/${a.Name}/?smid=${a.Id}&AFNO=1-94033`,
          embedUrl: `https://hybridclient.naiadsystems.com/purecam?performer=${a.Name}&widescreen=true`,
          displayName: a.Name, uniqueModelId: a.Id,
          profilePictureUrl: thumb, profilePictureFullUrl: full,
        };
      });

      // Filter gold tab client-side
      const filtered = tabVal === "gold" ? mapped.filter(p => p.goldShow) : mapped;
      setPerformers(prev => append ? [...prev, ...filtered] : filtered);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPerformers([]); setPage(1); setHasMore(true);
    fetchPerformers(tab, 1, false);
  }, [tab]);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchPerformers(tab, next, true);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fafafa" }}>
      {preRollP && <PreRollAd onComplete={() => { setEmbedP(preRollP); setPreRollP(null); }} />}
      {embedP && <CamEmbedModal performer={embedP} allPerformers={performers} onClose={() => setEmbedP(null)} />}

      {/* Header */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #eee", px: { xs: 2, md: 6 }, py: 3 }}>
        <Button component={Link} to="/" startIcon={<ArrowBackIcon />} sx={{ mb: 1, color: "#FF4196" }}>
          Home
        </Button>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              🏆 Best Cam Girls
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
              <LiveDot />
              <Typography variant="body2" sx={{ color: "#4CAF50", fontWeight: 600 }}>Live Now</Typography>
              {performers.length > 0 && (
                <Chip label={`${performers.length}+ performers`} size="small"
                  sx={{ bgcolor: "#fff0f7", color: "#FF4196", border: "1px solid #FF4196", fontWeight: 600 }} />
              )}
            </Box>
          </Box>
        </Box>

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mt: 2, "& .MuiTab-root": { fontWeight: 700, textTransform: "none", minWidth: 100 },
            "& .Mui-selected": { color: "#FF4196 !important" },
            "& .MuiTabs-indicator": { backgroundColor: "#FF4196" } }}
          variant="scrollable" scrollButtons="auto"
        >
          {TABS.map(t => <Tab key={t.value} label={t.label} value={t.value} />)}
        </Tabs>
      </Box>

      {/* Grid */}
      <Box sx={{ px: { xs: 1, md: 4 }, py: 3, maxWidth: 1400, mx: "auto" }}>
        <Grid container spacing={2}>
          {loading && performers.length === 0
            ? Array.from({ length: 12 }).map((_, i) => (
                <Grid item xs={6} sm={4} md={3} key={i}>
                  <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
                </Grid>
              ))
            : performers.map((p, i) => (
                <Grid item xs={6} sm={4} md={3} key={p.id || i}>
                  <PerformerTile onClick={() => setPreRollP(p)}>
                    <Box component="img"
                      src={p.gallery[0] || p.full || p.thumb}
                      alt={`${p.name} best cam girl`}
                      sx={{ width: "100%", height: 280, objectFit: "cover",
                        transition: "transform 0.3s ease", display: "block" }}
                      onError={e => { e.target.src = p.thumb || p.full || ""; }}
                    />

                    {/* Rank badge */}
                    {p.rank && p.rank <= 20 && (
                      <RankBadge rank={p.rank}>
                        {p.rank <= 3 ? ["🥇","🥈","🥉"][p.rank-1] : `#${p.rank}`}
                      </RankBadge>
                    )}

                    {/* Live badge */}
                    <Box sx={{ position: "absolute", top: 8, right: 8,
                      bgcolor: "rgba(76,175,80,0.9)", borderRadius: "12px",
                      px: 1, py: 0.3, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <LiveDot style={{ marginRight: 2 }} />
                      <Typography sx={{ color: "#fff", fontSize: "0.68rem", fontWeight: 700 }}>LIVE</Typography>
                    </Box>

                    {/* Gold badge */}
                    {p.goldShow && (
                      <Chip label="🥇 GOLD" size="small"
                        sx={{ position: "absolute", top: 36, right: 8,
                          bgcolor: "#FFD700", color: "#000", fontWeight: 700, fontSize: "0.62rem", height: 18 }} />
                    )}

                    {/* HD badge */}
                    {p.hdStream && (
                      <Chip label="HD" size="small"
                        sx={{ position: "absolute", bottom: 48, right: 8,
                          bgcolor: "rgba(33,150,243,0.9)", color: "#fff", fontWeight: 700, fontSize: "0.62rem", height: 18 }} />
                    )}

                    <Overlay className="overlay">
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <Avatar src={p.thumb} sx={{ width: 26, height: 26 }} />
                        <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.88rem" }}>{p.name}</Typography>
                      </Box>
                      {p.age && (
                        <Typography sx={{ color: "#ddd", fontSize: "0.73rem", mb: 1 }}>
                          {p.age} yrs{p.ethnicity ? ` · ${p.ethnicity}` : ""}
                        </Typography>
                      )}
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button size="small" variant="contained"
                          sx={{ background: "linear-gradient(45deg,#e91e63,#9c27b0)",
                            borderRadius: "20px", fontSize: "0.68rem", py: 0.4, px: 1.5 }}
                          onClick={e => { e.stopPropagation(); setPreRollP(p); }}>
                          <LiveTvIcon sx={{ fontSize: 12, mr: 0.4 }} /> Watch
                        </Button>
                        <Button size="small" variant="contained"
                          sx={{ background: "linear-gradient(90deg,#7c3aed,#a855f7)", color: "#fff",
                            borderRadius: "20px", fontSize: "0.68rem", py: 0.4, px: 1.5,
                            '&:hover': { background: "linear-gradient(90deg,#6d28d9,#9333ea)" } }}
                          onClick={e => { e.stopPropagation(); window.open(p.chatRoomUrl, "_blank"); }}>
                          💬 Chat
                        </Button>
                      </Box>
                    </Overlay>
                  </PerformerTile>
                </Grid>
              ))}
        </Grid>

        {hasMore && !loading && performers.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Button variant="contained"
              sx={{ background: "linear-gradient(45deg,#FF6B6B,#FF2BAA)",
                borderRadius: "50px", px: 5, py: 1.5, fontWeight: 700 }}
              onClick={loadMore}>
              Load More
            </Button>
          </Box>
        )}

        {/* SEO content */}
        <Box sx={{ mt: 6, maxWidth: 800, mx: "auto" }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            The Best Live Cam Girls Online Right Now
          </Typography>
          <Typography variant="body1" sx={{ color: "#666", mb: 2 }}>
            Browse our top-rated live cam girls, all streaming in real-time. From trending newcomers to established fan favorites,
            every performer is live and ready to chat. Filter by Trending, Top Rated, New Girls, or Gold Shows.
          </Typography>
          <Typography variant="body2" sx={{ color: "#888", mb: 3 }}>
            All performers are 18+, verified, and streaming in HD. Free to watch — private shows and tipping available.
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {["latina-cam-girls","blonde-cam-girls","ebony-cam-girls","asian-cam-girls",
              "milf-cam-girls","teen-cam-girls","brunette-cam-girls","mature-cam-girls"].map(slug => (
              <Chip key={slug} label={slug.replace("-cam-girls","").replace("-"," ")}
                component={Link} to={`/category/${slug}`} clickable
                sx={{ bgcolor: "#fff0f7", color: "#FF4196", border: "1px solid #FF4196",
                  fontWeight: 600, textTransform: "capitalize",
                  "&:hover": { bgcolor: "#FF4196", color: "#fff" } }} />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TopPerformersPage;
